import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Search, Plus, Trash2, Eye, FlaskConical } from "lucide-react";
import { toast } from "sonner";

export default function FirmSubstances({ firmId, canEdit }) {
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadSubs = () => api.get(`/firms/${firmId}/un-substances`).then(({ data }) => setSubs(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { loadSubs(); }, [firmId]); // eslint-disable-line

  const search = async () => {
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    try { const { data } = await api.get(`/un-catalog/search?q=${encodeURIComponent(q.trim())}`); setResults(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };
  const link = async (record_id) => {
    try { const { data } = await api.post(`/firms/${firmId}/un-substances`, { record_id }); toast.success(data.already ? "Bu kayıt zaten bağlı." : "Madde firmaya bağlandı."); loadSubs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unlink = async (record_id) => {
    try { await api.delete(`/firms/${firmId}/un-substances/${record_id}`); toast.success("Bağlantı kaldırıldı."); loadSubs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = async (record_id) => {
    try { const { data } = await api.get(`/un-catalog/${record_id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const linkedIds = new Set((subs || []).map((s) => s.record_id));

  return (
    <Card className="border-slate-200"><CardContent className="pt-6 space-y-5">
      <div>
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-sky-600" /> İşletmenin İştigal Ettiği Tehlikeli Maddeler</h3>
        <p className="text-xs text-slate-500 mt-1">UN numarası tek başına benzersiz değildir; aynı UN'nin farklı kayıtları ayrı seçeneklerdir. Bağlantı seçilen katalog kayıt ID üzerinden kurulur.</p>
      </div>

      {canEdit && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input placeholder="UN no veya madde adı ile ara (örn. 1202 veya dizel)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="un-search-input" />
            <Button onClick={search} disabled={searching} className="bg-sky-600 hover:bg-sky-700" data-testid="un-search-btn">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
          </div>
          {results !== null && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="un-search-results">
              {results.length === 0 ? <p className="text-sm text-slate-400 p-3">Sonuç yok.</p> : results.map((r) => (
                <div key={r.record_id} className="flex items-center justify-between gap-2 p-2.5 text-sm" data-testid={`un-result-${r.record_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-mono-tmgd font-semibold text-slate-800">UN {r.un_no}</span>{r.packing_group && <StatusBadge tone="info">PG {r.packing_group}</StatusBadge>}</div>
                    <div className="text-xs text-slate-500 truncate" title={r.name}>{r.name}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(r.record_id)} title="Detay"><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => link(r.record_id)} disabled={linkedIds.has(r.record_id)} data-testid={`un-link-${r.record_id}`}><Plus className="h-3.5 w-3.5 mr-1" /> {linkedIds.has(r.record_id) ? "Bağlı" : "Bağla"}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Bağlı Maddeler</div>
        {subs === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> : (
          subs.length === 0 ? <p className="text-sm text-slate-400">Henüz madde bağlanmadı.</p> : (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {subs.map((s) => (
                <div key={s.record_id} className="flex items-center justify-between gap-2 p-2.5 text-sm" data-testid={`un-linked-${s.record_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-mono-tmgd font-semibold text-slate-800">UN {s.un_no}</span>{s.packing_group && <StatusBadge tone="info">PG {s.packing_group}</StatusBadge>}</div>
                    <div className="text-xs text-slate-500 truncate" title={s.name}>{s.name}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(s.record_id)} data-testid={`un-detail-${s.record_id}`} title="Tüm ADR detayları"><Eye className="h-4 w-4" /></Button>
                    {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => unlink(s.record_id)} data-testid={`un-unlink-${s.record_id}`} title="Bağlantıyı kaldır"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">UN {detail?.un_no} — Katalog Detayı</DialogTitle>
            <DialogDescription>Seçilen katalog kaydının tüm ADR sütunları ve kaynak bilgileri.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-400">Katalog kayıt ID: {detail.record_id}</div>
              <div className="font-medium text-slate-800">{detail.name}</div>
              {detail.name2 && detail.name2 !== detail.name && (
                <div className="rounded bg-amber-50 border border-amber-200 p-2 text-xs"><span className="font-semibold">İkinci sayfadaki isim:</span> {detail.name2}</div>
              )}
              {detail.merged_note && <div className="rounded bg-slate-50 border border-slate-200 p-2 text-xs"><span className="font-semibold">Birleşik hücre notu:</span> {detail.merged_note}</div>}
              {detail.source_note && <div className="rounded bg-slate-50 border border-slate-200 p-2 text-xs"><span className="font-semibold">Kaynak notu:</span> {detail.source_note}</div>}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {(detail.adr_columns || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 p-2">
                    <div className="col-span-2 text-xs font-semibold text-slate-500">{c.label}</div>
                    <div className="col-span-3 text-xs text-slate-800 break-words">{c.value || <span className="text-slate-300">—</span>}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-slate-400">Kaynak sayfalar — PDF: {detail.source_pages?.pdf_first || "—"}{detail.source_pages?.pdf_cont ? `/${detail.source_pages.pdf_cont}` : ""} · Kitap: {detail.source_pages?.book_first || "—"}{detail.source_pages?.book_cont ? `/${detail.source_pages.book_cont}` : ""}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}
